import { NetworkStatus, useApolloClient, useQuery } from '@apollo/client'
import { Typography } from '@mui/material'
import { StyledContainer } from 'candidate/components/styles'
import { CandidateMeQuery } from 'candidate/graphql/candidate.gql'
import { GetCandidateProfileForCandidate } from 'candidate/graphql/generatedProfile.gql'
import { useState } from 'react'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import { ReactComponent as EmptyProfile } from 'shared/assets/new_empty_profile.svg'
import UnsavedChangesModal from 'shared/components/UnsavedChangesModal/UnsavedChangesModal'
import UnsavedChangesModalNavBlocker from 'shared/components/UnsavedChangesModal/UnsavedChangesModalNavBlocker'
import {
    Separator,
    StyledCard,
    StyledEmptyCardContent,
    TitleContainer,
} from 'shared/style/styled'
import AboutMe from './AboutMe'
import AdditionalInformation from './AdditionalInformation'
import Bio from './Bio'
import DocumentUpload from './DocumentUpload'
import Education from './Education'
import References from './References'
import TestScores from './TestScores'
import WorkExperience from './WorkExperience'
import { PROFILE_SECTIONS } from './types'

const Profile = () => {
    usePageTitle('Profile')
    const { data: userData } = useQuery(CandidateMeQuery, {
        fetchPolicy: 'network-only',
        onError(error) {
            console.log('error', error)
        },
    })

    const { data, refetch, networkStatus } = useQuery(
        GetCandidateProfileForCandidate,
        { variables: { userId: userData?.candidateMe?.user?.id ?? '' } }
    )

    const { cache } = useApolloClient()

    const handleRefetch = async () => {
        // https://github.com/apollographql/apollo-client/issues/7195
        const { data } = await refetch()

        // remove the old data from the cache
        cache.evict({
            id: `Candidate:${data.candidate?.user?.id}`,
        })

        // remove related but no longer used entities
        cache.gc()

        // manually execute the same query with the new data
        cache.writeQuery({
            query: GetCandidateProfileForCandidate,
            variables: { userId: data.candidate?.user?.id || '' },
            data,
        })
    }

    const isProfileCompleted =
        data?.candidate?.candidateIntakes &&
        data?.candidate?.candidateIntakes?.length > 1 &&
        data?.candidate?.candidateIntakes.every(
            (intake) => intake.completionDate !== null
        )

    const [editSection, setEditSection] = useState<PROFILE_SECTIONS | null>(
        null
    )
    const [nextEditSection, setNextEditSection] =
        useState<PROFILE_SECTIONS | null>(null)
    const [unsavedChangesExist, setUnsavedChangesExist] = useState(false)

    const [modalOpen, setModalOpen] = useState(false)

    const handleSetEdit = (nextState: PROFILE_SECTIONS | null) => {
        if (
            networkStatus !== NetworkStatus.refetch &&
            nextState !== editSection &&
            unsavedChangesExist
        ) {
            setModalOpen(true)
            setNextEditSection(nextState)
        } else {
            setEditSection(nextState)
        }
    }

    const closeEditWithoutModal = () => {
        setEditSection(null)
        setNextEditSection(null)
        setUnsavedChangesExist(false)
    }

    return (
        <StyledContainer>
            <TitleContainer>
                <Typography variant="h2">Profile</Typography>
            </TitleContainer>
            {/* this unsaved changes modal is opened manually when switching which section we are editing */}
            <UnsavedChangesModal
                open={modalOpen}
                onLeaveClick={() => {
                    setEditSection(nextEditSection)
                    setNextEditSection(null)
                    setModalOpen(false)
                    setUnsavedChangesExist(false)
                }}
                onStayClick={() => {
                    setNextEditSection(null)
                    setModalOpen(false)
                }}
            />
            {/* this unsaved changes modal blocks navigation to other pages when unsaved changes exist */}
            <UnsavedChangesModalNavBlocker
                unsavedChangesExist={unsavedChangesExist}
            />
            <StyledCard>
                {!isProfileCompleted ? (
                    <StyledEmptyCardContent>
                        <EmptyProfile />
                        <Typography variant="body1">
                            Please complete all questionnaire tasks to view your
                            profile.
                        </Typography>
                    </StyledEmptyCardContent>
                ) : (
                    <>
                        <Bio
                            id={data?.candidate?.user?.id || ''}
                            loading={false}
                            phone={data?.candidate?.user?.phone || ''}
                            profileImageKey={
                                data?.candidate?.user?.profileImageKey || ''
                            }
                            email={data?.candidate?.user?.email || ''}
                            linkedinUrl={data?.candidate?.linkedinUrl || ''}
                            refetch={handleRefetch}
                            edit={editSection === PROFILE_SECTIONS.BIO}
                            setEdit={handleSetEdit}
                            closeEditWithoutModal={closeEditWithoutModal}
                            setUnsavedChangesExist={setUnsavedChangesExist}
                        />
                        <Separator />
                        <AboutMe
                            loading={false}
                            data={data}
                            refetch={handleRefetch}
                            edit={editSection === PROFILE_SECTIONS.ABOUT_ME}
                            setEdit={handleSetEdit}
                            closeEditWithoutModal={closeEditWithoutModal}
                            setUnsavedChangesExist={setUnsavedChangesExist}
                        />
                        <Separator />
                        <Education
                            loading={false}
                            data={data}
                            refetch={handleRefetch}
                            edit={editSection === PROFILE_SECTIONS.EDUCATION}
                            setEdit={handleSetEdit}
                            closeEditWithoutModal={closeEditWithoutModal}
                            setUnsavedChangesExist={setUnsavedChangesExist}
                        />
                        <Separator />
                        <TestScores
                            testScores={data.candidate?.testScores}
                            edit={editSection}
                            setEdit={setEditSection}
                            closeEditWithoutModal={closeEditWithoutModal}
                            setUnsavedChangesExist={setUnsavedChangesExist}
                            refetch={refetch}
                        />
                        <Separator />
                        <WorkExperience
                            loading={false}
                            data={data}
                            refetch={handleRefetch}
                            edit={
                                editSection === PROFILE_SECTIONS.WORK_EXPERIENCE
                            }
                            setEdit={handleSetEdit}
                            closeEditWithoutModal={closeEditWithoutModal}
                            setUnsavedChangesExist={setUnsavedChangesExist}
                        />
                        <Separator />
                        <AdditionalInformation
                            loading={false}
                            data={data}
                            refetch={handleRefetch}
                            edit={
                                editSection ===
                                PROFILE_SECTIONS.ADDITIONAL_INFORMATION
                            }
                            setEdit={handleSetEdit}
                            closeEditWithoutModal={closeEditWithoutModal}
                            setUnsavedChangesExist={setUnsavedChangesExist}
                        />
                        <Separator />
                        <References
                            loading={false}
                            data={data}
                            refetch={handleRefetch}
                            edit={editSection === PROFILE_SECTIONS.REFERENCES}
                            setEdit={handleSetEdit}
                            closeEditWithoutModal={closeEditWithoutModal}
                            setUnsavedChangesExist={setUnsavedChangesExist}
                        />
                        <Separator />
                        <DocumentUpload
                            loading={false}
                            id={data?.candidate?.user?.id || ''}
                            data={data}
                            refetch={handleRefetch}
                        />
                    </>
                )}
            </StyledCard>
        </StyledContainer>
    )
}

export default Profile
